export type OwnerNotificationRouteInput = {
  type?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  actionUrl?: string | null;
};

const OWNER_ROUTES = new Set([
  'communications',
  'messages',
  'financials',
  'statements',
  'tax-documents',
  'documents',
  'approvals',
  'maintenance',
  'inspections',
  'properties',
  'reports',
  'audit-trail',
  'settings',
]);

export function normalizeOwnerActionUrl(
  url: string | null | undefined,
  currentOrigin?: string | null,
): string | null {
  const raw = String(url ?? '').trim();
  if (!raw || /^javascript:/i.test(raw)) return null;

  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);
      if (currentOrigin && parsed.origin === currentOrigin) {
        return normalizeOwnerActionUrl(`${parsed.pathname}${parsed.search}${parsed.hash}`, currentOrigin);
      }
    } catch {
      return null;
    }
    return raw;
  }

  const normalized = raw.startsWith('/') ? raw : `/${raw}`;
  const path = (normalized.split(/[?#]/)[0] || '').replace(/\/+$/, '');
  const route = path.split('/').filter(Boolean)[0] ?? '';
  return OWNER_ROUTES.has(route) ? normalized : null;
}

export function ownerNotificationFallbackTarget(input: OwnerNotificationRouteInput | string): string {
  const type = typeof input === 'string' ? input : `${input.type ?? ''} ${input.referenceType ?? ''}`;
  const value = type.toUpperCase();
  const referenceId = typeof input === 'string' ? null : String(input.referenceId ?? '').trim() || null;

  if (value.includes('OWNER_PAYOUT_REMINDER')) {
    return '/settings?panel=owner-payout-settings&focus=payout-setup';
  }
  // Year-end statements live under Tax documents; check before the monthly statement match.
  if (value.includes('TAX_STATEMENT')) return ownerRecordTarget('/tax-documents', 'statementId', referenceId);
  if (value.includes('STATEMENT')) return ownerRecordTarget('/statements', 'statementId', referenceId);
  if (value.includes('DOCUMENT')) return ownerRecordTarget('/documents', 'documentId', referenceId);
  if (value.includes('APPROVAL')) return ownerRecordTarget('/approvals', 'maintenanceId', referenceId);
  if (value.includes('MAINTENANCE')) return ownerRecordTarget('/maintenance', 'maintenanceId', referenceId);
  if (value.includes('INSPECTION') || value.includes('SELF_CHECK')) {
    return ownerRecordTarget('/inspections', 'inspectionId', referenceId);
  }
  if (value.includes('OWNER_FUNDING') || value.includes('FUNDING_REQUEST')) {
    return ownerRecordTarget('/financials?tab=funding', 'fundingRequestId', referenceId);
  }
  if (value.includes('PAYOUT') || value.includes('PAYMENT') || value.includes('RENT') || value.includes('FEE')) {
    return '/financials';
  }
  if (value.includes('PROPERTY') || value.includes('ASSIGNMENT')) {
    return ownerRecordTarget('/properties', 'propertyId', referenceId);
  }
  if (value.includes('REPORT') || value.includes('SUMMARY')) return '/reports';
  if (value.includes('AUDIT') || value.includes('ACCESS')) return '/audit-trail';
  // OWNER_MESSAGE references the owner record today, not a persisted conversation.
  if (value.includes('MESSAGE')) return '/messages?tab=inbox';
  return '/messages';
}

export function resolveOwnerNotificationTarget(
  notification: OwnerNotificationRouteInput,
  currentOrigin?: string | null,
): string {
  const actionUrl = normalizeOwnerActionUrl(notification.actionUrl, currentOrigin);
  const genericUpdatesTarget = actionUrl === '/messages' || actionUrl === '/messages/';
  if (actionUrl && !genericUpdatesTarget) return actionUrl;
  return ownerNotificationFallbackTarget(notification);
}

export function ownerNotificationActionLabel(target: string): string {
  if (target.includes('owner-payout-settings')) return 'Open payout setup';
  if (target.includes('tab=funding')) return 'Review funding request';
  if (target.startsWith('/financials')) return 'Review financials';
  if (target.startsWith('/statements')) return 'View statement';
  if (target.startsWith('/tax-documents')) return 'View tax document';
  if (target.startsWith('/documents')) return 'Open document';
  if (target.startsWith('/approvals')) return 'Review approval';
  if (target.startsWith('/maintenance')) return 'Open maintenance';
  if (target.startsWith('/inspections')) return 'Open inspection';
  if (target.startsWith('/properties')) return 'View property';
  if (target.startsWith('/reports')) return 'Open reports';
  if (target.startsWith('/audit-trail')) return 'Open audit trail';
  return 'Open update';
}

function ownerRecordTarget(path: string, parameter: string, referenceId: string | null): string {
  if (!referenceId) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${parameter}=${encodeURIComponent(referenceId)}`;
}
